import { useState } from "react";
import { Layout, Button, Typography, Avatar, Drawer, Grid } from "antd";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { userSelector, logout } from "../../features/authSlice";
import {
  UserOutlined,
  FileTextOutlined,
  LogoutOutlined,
  SearchOutlined,
  MenuOutlined,
} from "@ant-design/icons";

const { Header, Content, Footer } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const NAV_ITEMS = [
  { icon: <SearchOutlined />, label: "Buscar Índice", path: "/portal/search-index" },
  { icon: <FileTextOutlined />, label: "Documentos",   path: "/portal/documents" },
  { icon: <UserOutlined />,    label: "Mi Perfil",     path: "/portal/profile" },
];

const NavBtn = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", gap: 6,
      background: "none", border: "none", cursor: "pointer",
      padding: "6px 14px", borderRadius: 6,
      color: active ? "#ffffff" : "rgba(255,255,255,0.7)",
      fontWeight: active ? 600 : 400, fontSize: 14,
      backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
      transition: "all 0.2s", whiteSpace: "nowrap", width: "100%",
    }}
  >
    {icon}{label}
  </button>
);

const PortalLayout = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const dispatch   = useDispatch();
  const user       = useSelector(userSelector);
  const screens    = useBreakpoint();
  const isMobile   = !screens.md;
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/portal/login");
  };

  const goTo = (path) => { navigate(path); setOpen(false); };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#1d3557", padding: isMobile ? "0 16px" : "0 24px", height: 64,
      }}>
        {/* Logo */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flexShrink: 0 }}
          onClick={() => navigate("/portal/search-index")}
        >
          <img
            src="/logo.png" alt="Notaría"
            style={{ height: 32, objectFit: "contain", filter: "brightness(0) invert(1)" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          {!isMobile && (
            <Text style={{ color: "white", fontSize: 15, fontWeight: 600, whiteSpace: "nowrap" }}>
              Portal de Clientes
            </Text>
          )}
        </div>

        {isMobile ? (
          /* Mobile: avatar + hamburger */
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 13 }}>
              {user.name || user.email}
            </Text>
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: "white", fontSize: 20 }} />}
              onClick={() => setOpen(true)}
            />
          </div>
        ) : (
          /* Desktop: nav horizontal */
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {NAV_ITEMS.map((item) => (
              <NavBtn
                key={item.path}
                icon={item.icon} label={item.label}
                active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              />
            ))}
            <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.2)", margin: "0 8px" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar icon={<UserOutlined />} size="small" style={{ background: "rgba(255,255,255,0.2)" }} />
              <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, whiteSpace: "nowrap" }}>
                {user.name || user.email}
              </Text>
            </div>
            <Button
              type="text" icon={<LogoutOutlined />} onClick={handleLogout}
              style={{ color: "rgba(255,255,255,0.65)", marginLeft: 4 }}
            >
              Salir
            </Button>
          </div>
        )}
      </Header>

      {/* Drawer mobile */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar icon={<UserOutlined />} style={{ background: "#1d3557" }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{user.name || "—"}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{user.email}</div>
            </div>
          </div>
        }
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        width={260}
        styles={{ body: { padding: "16px 0" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "0 12px" }}>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.path}
              onClick={() => goTo(item.path)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 16px", borderRadius: 8, border: "none",
                background: location.pathname === item.path ? "#e8f0fe" : "transparent",
                color: location.pathname === item.path ? "#1d3557" : "#333",
                fontWeight: location.pathname === item.path ? 600 : 400,
                fontSize: 15, cursor: "pointer", textAlign: "left", width: "100%",
              }}
            >
              {item.icon}{item.label}
            </button>
          ))}
          <div style={{ height: 1, background: "#f0f0f0", margin: "8px 0" }} />
          <button
            onClick={() => { setOpen(false); handleLogout(); }}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 16px", borderRadius: 8, border: "none",
              background: "transparent", color: "#e63946",
              fontSize: 15, cursor: "pointer", textAlign: "left", width: "100%",
            }}
          >
            <LogoutOutlined />Cerrar sesión
          </button>
        </div>
      </Drawer>

      <Content style={{
        padding: isMobile ? "16px 12px" : "32px 24px",
        background: "#f0f2f5",
        minHeight: "calc(100vh - 110px)",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Outlet />
        </div>
      </Content>

      <Footer style={{
        textAlign: "center", background: "#1d3557",
        color: "rgba(255,255,255,0.6)",
        padding: isMobile ? "10px 16px" : "12px 24px",
        fontSize: isMobile ? 11 : 13,
      }}>
        Notaría Juan Carlos Cabezas Cepeda · Puerto Montt · Digital Notary
      </Footer>
    </Layout>
  );
};

export default PortalLayout;
